import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const ProbationCX: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "ProbationCX.sections.executiveSummary" },
    { id: "paper-challenges", labelKey: "ProbationCX.sections.paperChallenges" },
    { id: "digital-matters", labelKey: "ProbationCX.sections.digitalMatters" },
    { id: "how-works", labelKey: "ProbationCX.sections.howWorks" },
    { id: "use-cases", labelKey: "ProbationCX.sections.useCases" },
    { id: "benefits", labelKey: "ProbationCX.sections.benefits" },
    { id: "implementation", labelKey: "ProbationCX.sections.implementation" },
    { id: "conclusion", labelKey: "ProbationCX.sections.conclusion" },
    { id: "references", labelKey: "ProbationCX.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | ProbationCX
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            From Paper to Digital: Smarter Referrals for Justice-Involved Individuals
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Probation agencies across the country still rely on paper-based, manual processes to refer justice-involved individuals to treatment, education, and support programs. These outdated systems result in delays, lost paperwork, compliance challenges, and reduced accountability.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="10 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "From Paper to Digital: Smarter Referrals for Justice-Involved Individuals", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "paper-challenges" && "🤖 1. The Challenges of Paper-Based Referrals"}
                {s.id === "digital-matters" && "📊 2. Why Digital Referrals Matter"}
                {s.id === "how-works" && "💡 3. How ProbationCX Works"}
                {s.id === "use-cases" && "⚠️ 4. Use Cases in Action"}
                {s.id === "benefits" && "🛠️ 5. Benefits for Agencies, Providers, and Individuals"}
                {s.id === "implementation" && "🛠️ 6. Implementation Roadmap"}
                {s.id === "conclusion" && "✅ 7. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Probation agencies across the country still rely on paper-based, manual processes to refer justice-involved individuals to treatment, education, and support programs. These outdated systems result in delays, lost paperwork, compliance challenges, and reduced accountability.
                    </p>
                    <br />
                    <p>
                      ProbationCX replaces these manual processes with a secure, digital referral platform. By standardizing forms, enabling two-way visibility, and automating notifications, ProbationCX ensures timely referrals, better compliance, and improved outcomes for justice-involved individuals [1][2].
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> Paper forms, phone calls, and faxes inefficiencies delay access to critical services and increase recidivism risk.</p>
                    </div>
                  </>
                )}
                {s.id === "paper-challenges" && (
                  <>
                    <p>
                      Paper forms, phone calls, and faxes still dominate referral workflows in many probation departments. This creates significant problems:
                    </p>
                    <br />
                    <ul>
                      <li>- Lost or incomplete referrals [1].</li>
                      <li>- No visibility into referral status for officers or providers.</li>
                      <li>- Difficult coordination across agencies and treatment programs.</li>
                      <li>- Increased administrative burden on probation staff.</li>
                    </ul>
                    <br />
                    <p>
                      These inefficiencies delay access to critical services and increase recidivism risk.
                    </p>
                  </>
                )}
                {s.id === "digital-matters" && (
                  <>
                    <p>
                      The justice system is under pressure to improve efficiency, reduce recidivism, and ensure accountability. Digital transformation helps by ensuring:
                    </p>
                    <br />
                    <ul>
                      <li>- Speed: Faster referrals mean individuals access services more quickly.</li>
                      <li>- Accuracy: Standardized forms reduce errors.</li>
                      <li>- Equity: Consistent processes across individuals and programs.</li>
                      <li>- Accountability: Every referral is tracked, monitored, and auditable [2].</li>
                    </ul>
                  </>
                )}
                {s.id === "how-works" && (
                  <>
                    <ul>
                      <li>- Standardized Digital Referrals – ensure consistency across cases.</li>
                      <li>- Two-Way Visibility – probation officers and providers see real-time status.</li>
                      <li>- Secure Information Exchange – sensitive data is protected.</li>
                      <li>- Automated Notifications – reduces missed appointments and manual follow-ups.</li>
                      <li>- Audit & Compliance Reporting – simplifies oversight for courts and agencies.</li>
                    </ul>
                  </>
                )}
                {s.id === "use-cases" && (
                  <>
                    <ul>
                      <li>- Substance Use & Mental Health Programs – secure, fast referrals to treatment.</li>
                      <li>- Education & Employment Prep – digital tracking of GED, training, job readiness.</li>
                      <li>- Reentry & Transition Support – connection to housing, counseling, and support.</li>
                      <li>- Probation & Supervision Check-ins – reminders and compliance documentation.</li>
                    </ul>
                  </>
                )}
                {s.id === "benefits" && (
                  <>
                    <ul>
                      <li>- Agencies: Reduced paperwork, improved compliance, streamlined reporting.</li>
                      <li>- Providers: Faster intake, fewer no-shows, clear communication.</li>
                      <li>- Individuals: Faster access to services, reduced confusion, improved reentry success.</li>
                    </ul>
                  </>
                )}
                {s.id === "implementation" && (
                  <>
                    <ol>
                      <li>1. Assess current referral workflows and identify inefficiencies.</li>
                      <li>2. Configure ProbationCX to mirror or improve agency processes.</li>
                      <li>3. Train probation officers, courts, and providers.</li>
                      <li>4. Launch pilots, gather feedback, and scale across programs.</li>
                      <li>5. Use analytics to monitor outcomes and strengthen reporting.</li>
                    </ol>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      ProbationCX enables probation departments to replace outdated, paper-based referrals with a secure digital platform. By doing so, agencies improve compliance, providers gain efficiency, and justice-involved individuals access critical services more quickly. Smarter referrals mean better outcomes and reduced recidivism [1][2][3].
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. Bureau of Justice Statistics (BJS). Probation and Parole in the United States. 2022. https://bjs.ojp.gov/library/publications/probation-and-parole-united-states-2022</li>
                      <li>2. National Institute of Justice (NIJ). Strategies to Reduce Recidivism. 2021. https://nij.ojp.gov/topics/articles/five-things-about-reentry</li>
                      <li>3. Council of State Governments Justice Center. Digital Solutions for Reentry. 2020. https://csgjusticecenter.org/publications/</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Average Time to Service Connection (Days)</li>
                        <li>• Referral Completion Rates (%)</li>
                        <li>• Recidivism Risk Reduction with Digital Referrals (%)</li>
                      </ul>
                      <p className="mt-2 text-xs text-slate-500">
                        Charts are illustrative, based on benchmarks reported by BJS and NIJ regarding average delays and completion rates in probation referrals.
                      </p>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Average Time to Service Connection</h5>
                        <img 
                          src="/Image/WhitePapers/ProbationCX_WhitePaper_New_Format/image1.png" 
                          alt="Average Time to Service Connection Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Referral Completion Rates</h5>
                        <img 
                          src="/Image/WhitePapers/ProbationCX_WhitePaper_New_Format/image2.png" 
                          alt="Referral Completion Rates Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Recidivism Risk Reduction with Digital Referrals</h5>
                        <img 
                          src="/Image/WhitePapers/ProbationCX_WhitePaper_New_Format/image3.png" 
                          alt="Recidivism Risk Reduction with Digital Referrals Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how digital transformation improves probation referral processes, reducing delays and improving outcomes for justice-involved individuals.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Paper-based processes create delays</li>
                <li>Digital referrals improve compliance</li>
                <li>Two-way visibility for all stakeholders</li>
                <li>Reduced recidivism through better access</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "paper-challenges" && "The Challenges of Paper-Based Referrals"}
                      {s.id === "digital-matters" && "Why Digital Referrals Matter"}
                      {s.id === "how-works" && "How ProbationCX Works"}
                      {s.id === "use-cases" && "Use Cases in Action"}
                      {s.id === "benefits" && "Benefits for Agencies, Providers, and Individuals"}
                      {s.id === "implementation" && "Implementation Roadmap"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "From Paper to Digital: Smarter Referrals for Justice-Involved Individuals." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "From Paper to Digital: Smarter Referrals for Justice-Involved Individuals." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "The Future of Referral Management in Healthcare", description: "From fax to AI-powered workflows that eliminate administrative waste and improve access.", route: "/whitepapers/referral-cx" },
              { title: "Digital Front Doors in Healthcare", description: "Why scheduling matters more than ever for patient experience and provider efficiency.", route: "/whitepapers/scheduling-cx" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs.", route: "/whitepapers/integrated-care" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="From Paper to Digital: Smarter Referrals for Justice-Involved Individuals"
        pdfPath="/PDF/WhitePapers/ProbationCX_WhitePaper.pdf"
      />
    </div>
  )
}

export default ProbationCX
